<template>
  <div class="py-6 px-8">
    <h-header>
      <template v-slot:actions>
        <v-btn color="primary" elevation="0" @click="openLinkModal">
          <v-icon left>mdi-link</v-icon>
          <span class="d-none d-md-flex body-2">{{ $t('customer.label.linkCustomerType') }}</span>
        </v-btn>
      </template>
    </h-header>

    <LinkAppointmentTypeChecklistFilters />

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="linkedServiceTypes"
        :loading="isLoading"
        item-key="id"
        class="elevation-0"
        hide-default-footer
        disable-pagination
        :no-data-text="$t('customer.label.noData')"
        loading-text="Carregando..."
      >
        <template #[`item.appointmentType`]="{ item }">
           <span class="body-2">{{ translateAppointmentType(item.appointment_type) }}</span>
        </template>

        <template #[`item.realEstate`]="{ item }">
          <span class="body-2">{{ item.real_estate_name || '-' }}</span>
        </template>

        <template #[`item.checklistConfigCode`]="{ item }">
           <span class="body-2">{{ item.checklist_name || '-' }}</span>
        </template>

        <template #[`item.baseReportRequired`]="{ item }">
          <v-icon v-if="item.base_report_required" small color="primary">mdi-check</v-icon>
          <span v-else class="body-2">-</span>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            small
            icon
            color="error"
            elevation="0"
            :disabled="!item.checklist_config_code"
            @click="openConfirmUnlinkModal(item)"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <LinkChecklistModal
      @success="handleSuccess"
      @error="handleError"
    />

    <ConfirmUnlinkModal
      :item="selectedItemToUnlink"
      @success="handleSuccess"
      @error="handleError"
    />

    <h-pagination :fetch="fetchWithFilters"/>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import LinkChecklistModal from './modal/LinkAppointmentTypeChecklistModal.vue'
import ConfirmUnlinkModal from './modal/UnlinkAppointmentTypeChecklistModal.vue'
import LinkAppointmentTypeChecklistFilters from './LinkAppointmentTypeChecklistFilters.vue'
import { isBaseReportEnabled } from '@/util/featureFlags'

export default {
  name: 'LinkedAppointmentTypeChecklists',
  components: {
    LinkChecklistModal,
    ConfirmUnlinkModal,
    LinkAppointmentTypeChecklistFilters,
  },
  data() {
    return {
      form: {
        appointment_type: null,
      },
      isChecklistModalOpen: false,
      isConfirmUnlinkOpen: false,
      selectedItemToUnlink: null,
      headers: [
        { text: this.$t('defaultServiceValues.label.serviceType'), value: 'appointmentType' },
        { text: this.$t('userChecklistModal.realEstateColumn'), value: 'realEstate', sortable: false },
        { text: this.$t('host.label.checklistLinked'), value: 'checklistConfigCode' },
        ...(isBaseReportEnabled()
          ? [{ text: this.$t('userChecklistModal.baseReport'), value: 'baseReportRequired', sortable: false }]
          : []),
        { text: '', align: 'end', value: 'actions', sortable: false },
      ],
    }
  },
  computed: {
    ...mapGetters({
      linkedServiceTypes: 'linkedServiceTypes',
      total: 'total',
      options: 'options',
      isLoading: 'loading/isLoading',
    }),
    visitTypes() {
      return [...new Set(this.linkedServiceTypes.map(item => item.appointmentType))]
        .filter(Boolean)
        .map(value => ({
          label: this.translateAppointmentType(value),
          value,
        }))
    },
  },
  watch: {
    'form.appointment_type'() {
      this.fetchWithFilters()
    },
  },
  methods: {
    ...mapActions([
      'unlinkAppointmentTypeChecklist',
      'fetchLinkedServiceTypes',
      'updateOptions',
      'loadUsedAppointmentTypes',
    ]),
    ...mapMutations(['openModal', 'closeAllModals']),
    openLinkModal() {
      this.openModal('LINK_CHECKLIST_MODAL')
    },
    openConfirmUnlinkModal(item) {
      const code =
        item.appointment_type ??
        item.appointmentType ??
        item.type ??
        item.value ??
        null

      this.selectedItemToUnlink = {
        id: item.id,
        appointment_type: code,
        typeLabel: code
          ? this.$t(`appointment.type.${code}`)
          : this.$t('appointment.type.undefined'),
      }

      this.openModal('CONFIRM_UNLINK_MODAL')
    },
    async handleSuccess(message) {
      if (message) this.$toast.success(message)
      this.isChecklistModalOpen = false
      this.isConfirmUnlinkOpen = false
      await this.fetchWithFilters()
    },

    handleError(errorMessage) {
      this.$toast.error(errorMessage)
    },

    translateAppointmentType(type) {
      return type
        ? this.$t(`appointment.type.${type}`)
        : this.$t('appointment.type.undefined')
    },

    async fetchWithFilters() {
      const filters = {}
      if (this.form.appointment_type) filters.appointmentType = this.form.appointment_type
      await this.fetchLinkedServiceTypes(filters)
    },

    onPageChange({ page, itemsPerPage }) {
      this.updateOptions({ page, itemsPerPage })
    },
  },
  mounted() {
    this.fetchWithFilters()
  },
}
</script>
